import Message from "../../Message";
import Modal from "../Modal";
import { postJson } from "../../utils";
import {
  getStripeElementsAppearance,
  getStripePaymentElementOptions,
  showStripeSubmissionError,
} from "../Checkout/checkout-utils";

const initBookNowPayLaterPaymentMethodUpdate = () => {
  const root = document.getElementById('bnpl-payment-method-update');
  if (!root) {
    return;
  }

  const openAddPaymentMethodButtons = document.querySelectorAll('[data-bnpl-open-add-payment-method]');
  const confirmPaymentMethodButton = root.querySelector('[data-bnpl-confirm-payment-method]');
  const paymentElementContainer = document.getElementById('bnpl-payment-method-update-element');
  let stripe = null;
  let elements = null;
  let setupIntent = null;
  let paymentElement = null;
  let setupIntentLoading = false;
  let paymentElementReady = false;
  let submitting = false;

  const errorContext = {
    paymentType: 'book_now_pay_later_payment_method_update',
    hostId: root.dataset.hostId,
    errorMessage: root.dataset.errorMessage,
  };

  const updateConfirmButton = () => {
    if (confirmPaymentMethodButton) {
      confirmPaymentMethodButton.disabled = !paymentElementReady || submitting;
      confirmPaymentMethodButton.classList.toggle('loading', submitting);
    }
  };

  updateConfirmButton();

  const removeStripeQueryParams = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('setup_intent');
    url.searchParams.delete('setup_intent_client_secret');
    url.searchParams.delete('redirect_status');
    window.history.replaceState({}, document.title, url.toString());
  };

  const finalizeReturnedSetupIntent = async () => {
    const url = new URL(window.location.href);
    const setupIntentId = url.searchParams.get('setup_intent');
    if (!setupIntentId || !root.dataset.confirmUrl) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append('setup_intent', setupIntentId);

      const data = await postJson(root.dataset.confirmUrl, formData);

      window.location.href = data.redirectUrl || root.dataset.returnUrl;
    } catch (error) {
      removeStripeQueryParams();
      Message.error(error.message);
      await openAddPaymentMethodModal();
    }
  };

  const createSetupIntent = async () => {
    if (setupIntent) {
      return setupIntent;
    }

    if (setupIntentLoading || !root.dataset.createSetupIntentUrl) {
      return null;
    }

    setupIntentLoading = true;

    try {
      const data = await postJson(root.dataset.createSetupIntentUrl);
      setupIntent = data.setupIntentPayload;
      return setupIntent;
    } catch (error) {
      Message.error(error.message);
      return null;
    } finally {
      setupIntentLoading = false;
    }
  };

  const getBillingDetails = () => ({
    name: root.dataset.billingName || '',
    email: root.dataset.billingEmail || '',
    phone: root.dataset.billingPhone || '',
  });

  const handlePaymentElementError = (error) => {
    const failedElement = paymentElement;
    paymentElement = null;
    elements = null;
    paymentElementReady = false;
    updateConfirmButton();
    if (failedElement) {
      failedElement.destroy();
    }
    showStripeSubmissionError(error, errorContext);
  };

  const mountPaymentElement = async () => {
    if (elements && paymentElement) {
      return true;
    }

    const setupIntentPayload = await createSetupIntent();
    if (!setupIntentPayload || !paymentElementContainer) {
      return false;
    }

    try {
      if (!stripe) {
        stripe = Stripe(root.dataset.stripePublishKey);
      }

      elements = stripe.elements({
        clientSecret: setupIntentPayload.client_secret,
        appearance: getStripeElementsAppearance(),
      });

      paymentElement = elements.create('payment', getStripePaymentElementOptions());
      const mountedElement = paymentElement;
      paymentElement.on('ready', () => {
        if (paymentElement === mountedElement) {
          paymentElementReady = true;
          updateConfirmButton();
        }
      });
      paymentElement.on('loaderror', ({ error }) => {
        if (paymentElement === mountedElement) {
          handlePaymentElementError(error);
        }
      });
      paymentElement.mount('#bnpl-payment-method-update-element');

      return true;
    } catch (error) {
      handlePaymentElementError(error);
      return false;
    }
  };

  const openAddPaymentMethodModal = async () => {
    Modal.closeModal('#bnplPaymentFailedModal');
    Modal.openModal('#bnplAddPaymentMethodModal');
    await mountPaymentElement();
  };

  openAddPaymentMethodButtons.forEach(button => {
    button.addEventListener('click', openAddPaymentMethodModal);
  });

  if (confirmPaymentMethodButton) {
    confirmPaymentMethodButton.addEventListener('click', async () => {
      if (submitting || !paymentElementReady || !stripe || !elements) {
        return;
      }

      submitting = true;
      updateConfirmButton();

      try {
        // Open wallets during the click, before any asynchronous preparation.
        const { error: submitError } = await elements.submit();
        if (submitError) {
          showStripeSubmissionError(submitError, errorContext);
          return;
        }

        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: root.dataset.returnUrl,
            payment_method_data: {
              billing_details: getBillingDetails(),
            },
          },
        });

        if (result.error) {
          showStripeSubmissionError(result.error, errorContext);
        }
      } catch (error) {
        showStripeSubmissionError(error, errorContext);
      } finally {
        submitting = false;
        updateConfirmButton();
      }
    });
  }

  finalizeReturnedSetupIntent();

  if (root.dataset.openFailed === '1') {
    Modal.openModal('#bnplPaymentFailedModal');
  } else if (root.dataset.openAddPaymentMethod === '1') {
    openAddPaymentMethodModal();
  }

  if (root.dataset.paymentMethodUpdated === '1') {
    Message.success(root.dataset.paymentMethodUpdatedMessage);
  }
};

export { initBookNowPayLaterPaymentMethodUpdate };
